<template>
  <div>
    <!-- App / control-panel post-install info (only when an app was deployed). -->
    <kvm-app-info v-if="server.app_info" :app-info="server.app_info" :app-name="server.app" class="mb-6" />

    <!-- Server summary + live metrics -->
    <gg-card class="mb-6">
      <gg-card-text class="pa-4">
        <div class="d-flex flex-wrap align-center mb-4">
          <kvm-logo :item="{ os: server.os }" :size="40" default-icon="mdi-server" class="mr-3" />
          <div class="mr-4">
            <div class="body-1 font-weight-medium">{{ server.os }}</div>
            <div class="mt-1">
              <v-chip x-small label class="mr-1">{{ server.plan }}</v-chip>
              <v-icon small class="pb-1" :color="statusChip.color" aria-hidden="true">mdi-circle</v-icon>
              <span class="pl-1 text-body-2">{{ statusChip.text }}</span>
            </div>
          </div>

          <div class="mr-auto my-2">
            <div class="vds-sublabel text-body-2 font-weight-bold">Root access</div>
            <kvm-copyable :value="sshCommand" :display="sshCommand" />
          </div>
        </div>

        <!-- Change the hostname — set on the running server (and kept across rebuilds). -->
        <v-dialog v-model="hostnameDialog.open" max-width="480">
          <v-card role="dialog" aria-modal="true" aria-labelledby="kvm-hostname-dialog-title">
            <v-card-title id="kvm-hostname-dialog-title" class="body-1 font-weight-medium">Change hostname</v-card-title>
            <v-card-text>
              <p class="mb-3">
                The server's own name, set on it right away. A rebuild keeps it. Use letters, numbers and
                hyphens, optionally with dots (e.g. <code>web1</code> or <code>web1.example.com</code>).
              </p>
              <v-text-field v-model.trim="hostnameDialog.value" label="Hostname" outlined dense autocomplete="off"
                            spellcheck="false" :rules="[v => hostnameValid(v) || 'Not a valid hostname.']"
                            @keyup.enter="hostnameValid(hostnameDialog.value) && saveHostname()" />
              <p v-if="!serverRunning" class="text-caption mb-0">The server must be running to change its hostname.</p>
              <v-alert v-if="hostnameDialog.error" dense text type="warning" role="alert" class="vds-form-alert mt-2 mb-0">
                {{ hostnameDialog.error }}
              </v-alert>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
              <v-spacer />
              <gg-btn text class="vds-dialog-btn" :disabled="hostnameDialog.saving" @click="hostnameDialog.open = false">Cancel</gg-btn>
              <gg-btn tile color="primary" class="vds-dialog-btn" :loading="hostnameDialog.saving"
                      :disabled="!serverRunning || !hostnameValid(hostnameDialog.value)" @click="saveHostname">Change</gg-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Set a new (customer-supplied) root password — kept only on the server
             (revealable in VPS Details). May reboot. -->
        <v-dialog v-model="passwordDialog" max-width="480" persistent>
          <v-card role="dialog" aria-modal="true" aria-labelledby="kvm-pwd-dialog-title">
            <v-card-title id="kvm-pwd-dialog-title" class="body-1 font-weight-medium">Change root password</v-card-title>
            <v-card-text>
              <p class="mb-3">
                Set a new root password for <strong>{{ server.hostname }}</strong>. The current
                password stops working once this is applied.
              </p>
              <v-text-field
                v-model="password"
                :type="reveal ? 'text' : 'password'"
                label="New root password"
                outlined dense
                :counter="128"
                :rules="passwordRules"
                autocomplete="new-password"
                persistent-hint
                hint="8–128 characters."
                @keyup.enter="passwordValid && resetPassword()"
              >
                <template v-slot:append>
                  <v-icon small class="mr-2 vds-icon-btn" :title="reveal ? 'Hide password' : 'Reveal password'"
                          :aria-label="reveal ? 'Hide password' : 'Reveal password'"
                          :aria-pressed="reveal ? 'true' : 'false'" @click="reveal = !reveal">
                    {{ reveal ? 'mdi-eye-off' : 'mdi-eye' }}
                  </v-icon>
                  <v-icon small class="vds-icon-btn" title="Copy password" aria-label="Copy password"
                          @click="copyPassword">mdi-content-copy</v-icon>
                </template>
              </v-text-field>
              <gg-btn text small color="primary" class="px-1 vds-dialog-btn" @click="generatePassword">
                <v-icon left small aria-hidden="true">mdi-auto-fix</v-icon>Generate strong password
              </gg-btn>
              <v-checkbox
                v-model="allowReboot"
                dense hide-details
                class="mt-3"
                label="Allow a brief reboot to apply the password if required"
              />
              <p class="vds-metric__sub text-caption mt-1 mb-0">
                Applies instantly when the guest agent is responding. If it isn't, applying the
                password needs a brief restart — only performed if you allow it above.
              </p>
              <v-alert v-if="formError" dense text type="warning" role="alert" class="vds-form-alert mt-3 mb-0">
                {{ formError }}
              </v-alert>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
              <v-spacer />
              <gg-btn text class="vds-dialog-btn" :disabled="resetting" @click="closePasswordDialog">Cancel</gg-btn>
              <gg-btn tile color="primary" class="vds-dialog-btn" :loading="resetting" :disabled="!passwordValid" @click="resetPassword">
                Set password
              </gg-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-row dense>
          <v-col cols="12" sm="6" md="4">
            <div class="vds-metric pa-3 d-flex flex-column">
              <div class="vds-metric__label text-caption mb-1">CPU usage</div>
              <div class="text-h6 font-weight-bold">{{ server.cpu_used_percent }}%</div>
              <kvm-sparkline v-if="cpuSeries.length" :value="cpuSeries" :color="primaryColor" class="mt-auto pt-2" />
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <div class="vds-metric pa-3 d-flex flex-column">
              <div class="vds-metric__label text-caption mb-1">Memory usage</div>
              <div class="text-h6 font-weight-bold">{{ memPercent }}%</div>
              <div class="vds-metric__sub text-caption">{{ memUsage }}</div>
              <kvm-sparkline v-if="memSeries.length" :value="memSeries" :color="primaryColor" class="mt-auto pt-2" />
            </div>
          </v-col>
          <!-- Traffic: in + out on one shared-scale chart. The totals are Proxmox's
               cumulative counters, which reset whenever the VM boots. -->
          <v-col cols="12" sm="6" md="4">
            <div class="vds-metric pa-3 d-flex flex-column">
              <div class="vds-metric__label text-caption mb-1">Traffic <span class="vds-metric__sub">· since last boot</span></div>
              <div class="d-flex flex-wrap">
                <div class="mr-4">
                  <span class="vds-legend-dot" :style="{ background: primaryColor }" aria-hidden="true" />
                  <span class="text-h6 font-weight-bold">{{ kvmSize(server.net_in) }}</span>
                  <span class="vds-metric__sub text-caption ml-1">in</span>
                </div>
                <div>
                  <span class="vds-legend-dot" :style="{ background: trafficOutColor }" aria-hidden="true" />
                  <span class="text-h6 font-weight-bold">{{ kvmSize(server.net_out) }}</span>
                  <span class="vds-metric__sub text-caption ml-1">out</span>
                </div>
              </div>
              <kvm-dual-sparkline v-if="netinSeries.length || netoutSeries.length"
                                  :a="netinSeries" :b="netoutSeries"
                                  :color-a="primaryColor" :color-b="trafficOutColor" class="mt-auto pt-2" />
            </div>
          </v-col>
        </v-row>
      </gg-card-text>
    </gg-card>

    <!-- Quick actions -->
    <v-row dense class="mb-6">
      <v-col v-for="t in actionTiles" :key="t.title" cols="12" sm="6" md="4">
        <gg-card class="vds-tile" :to="{ name: t.route, params: { hostingid: server.id } }">
          <gg-card-text class="d-flex align-center pa-3">
            <v-icon color="primary" class="mr-3" aria-hidden="true">{{ t.icon }}</v-icon>
            <div class="mr-auto">
              <div class="body-2 font-weight-medium">{{ t.title }}</div>
              <div class="vds-tile__sub text-caption">{{ t.sub }}</div>
            </div>
            <v-icon small aria-hidden="true">mdi-chevron-right</v-icon>
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>

    <!-- VPS details and Plan details: full-width cards, two columns of
         "Label: value" rows — the same format as the shared-hosting
         "Hosting Account Details" card. -->
    <v-row class="mb-2">
      <v-col cols="12">
        <gg-card>
          <gg-card-text class="pa-4">
            <div class="body-1 mb-4" role="heading" aria-level="2">
              <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-server</v-icon>
              <span class="ml-2 font-weight-medium">VPS Details</span>
            </div>
            <v-row>
              <v-col v-for="(col, ci) in columns(vpsDetails)" :key="ci" cols="12" md="6"
                     :class="ci === 0 ? 'pl-4 pr-md-9 py-0' : 'pl-md-9 pr-4 py-0'">
                <div v-for="row in col" :key="row.label" class="d-flex align-center vds-detail-row">
                  <span class="font-weight-bold mr-auto">{{ row.label }}:</span>
                  <!-- Root password: hidden until revealed (read from the server), plus Change. -->
                  <template v-if="row.rootPassword">
                    <v-progress-circular v-if="rootPw.loading" indeterminate size="14" width="2" color="primary" aria-label="Loading the root password" />
                    <template v-else>
                      <kvm-copyable v-if="rootPw.shown" :value="rootPw.value" :display="rootPw.value" />
                      <span v-else-if="rootPw.available === false" class="vds-detail__value--muted">Not on file</span>
                      <span v-else class="vds-rootpw-mask">••••••••••••</span>
                      <v-icon v-if="rootPw.shown && rootPw.current === false" small color="warning" class="ml-1"
                              title="This password was changed on the server itself, so it may no longer work. Use Change to set a new one."
                              aria-label="Changed on the server — may no longer work">mdi-alert</v-icon>
                      <v-icon v-if="rootPw.error" small color="warning" class="ml-1" :title="rootPw.error" :aria-label="rootPw.error">mdi-alert-circle-outline</v-icon>
                      <!-- Never a dead end: "Not on file" can always be checked again. -->
                      <gg-btn icon x-small class="ml-1" :aria-label="rootPwButton.label"
                              :title="serverRunning ? rootPwButton.label : 'The server must be running to show its root password'"
                              :disabled="!serverRunning && !rootPw.shown" @click="toggleRootPassword">
                        <v-icon x-small>{{ rootPwButton.icon }}</v-icon>
                      </gg-btn>
                      <!-- Copy without showing it. -->
                      <gg-btn v-if="rootPw.available !== false" icon x-small class="ml-1" aria-label="Copy root password"
                              :title="serverRunning || rootPw.value ? 'Copy' : 'The server must be running to copy its root password'"
                              :disabled="!serverRunning && !rootPw.value" @click="copyRootPassword">
                        <v-icon x-small>mdi-content-copy</v-icon>
                      </gg-btn>
                    </template>
                    <button type="button" class="vds-link-btn primary--text ml-3" aria-haspopup="dialog" @click="openPasswordDialog">Change</button>
                  </template>
                  <!-- Hostname: copy, plus Change (applied on the running server). -->
                  <template v-else-if="row.hostname">
                    <kvm-copyable :value="row.value" icon-start />
                    <button type="button" class="vds-link-btn primary--text ml-3" aria-haspopup="dialog" @click="openHostnameDialog">Change</button>
                  </template>
                  <kvm-copyable v-else-if="row.copy" :value="row.value" icon-start />
                  <span v-else :class="{ 'vds-detail__value--muted': row.muted }">{{ row.value }}</span>
                </div>
              </v-col>
            </v-row>
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <gg-card>
          <gg-card-text class="pa-4">
            <div class="body-1 mb-4" role="heading" aria-level="2">
              <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-account-settings</v-icon>
              <span class="ml-2 font-weight-medium">Plan Details</span>
            </div>
            <v-row>
              <v-col v-for="(col, ci) in planColumns" :key="ci" cols="12" md="6"
                     :class="ci === 0 ? 'pl-4 pr-md-9 py-0' : 'pl-md-9 pr-4 py-0'">
                <div v-for="row in col" :key="row.label" class="d-flex align-center vds-detail-row">
                  <span class="font-weight-bold mr-auto">{{ row.label }}:</span>
                  <!-- Sharing: same as the shared-hosting card (account access). -->
                  <template v-if="row.sharing">
                    <shared-service-tag v-if="billing && (billing.sharedaccount || billing.sharedaccess)" :service="billing" class="vds-plan-link" />
                    <router-link v-else :to="{ name: 'profile_access' }" class="vds-plan-link">Enable Sharing</router-link>
                  </template>
                  <template v-else>
                    <v-icon v-if="row.dot" small class="pb-1 mr-1" :color="row.dot" aria-hidden="true">mdi-circle</v-icon>
                    <span v-if="row.value" :class="{ 'vds-detail__value--muted': row.muted }">{{ row.value }}</span>
                    <router-link v-if="row.to" :to="row.to" class="vds-plan-link" :class="{ 'ml-4': row.value }">{{ row.link }}</router-link>
                    <a v-else-if="row.action" href="#" class="vds-plan-link" :class="{ 'ml-4': row.value }" @click.prevent="row.action">{{ row.link }}</a>
                  </template>
                </div>
              </v-col>
            </v-row>

            <!-- Auto-renew enable: not eligible / confirm (as on the other services). -->
            <gg-modal :is-active="autorenew.ineligible" @close="autorenew.ineligible = false">
              <template v-slot:toolbar>Enabling auto renew error</template>
              <template v-slot:body>Your service is not eligible to enable auto renew. Please contact Billing for assistance.</template>
              <template v-slot:actions><gg-btn @click="autorenew.ineligible = false">Close</gg-btn></template>
            </gg-modal>
            <gg-modal :is-active="autorenew.confirm" @close="autorenew.confirm = false">
              <template v-slot:toolbar>Enabling auto renew</template>
              <template v-slot:body>
                <p>This service will be set to auto-renew. Our system will automatically renew your account to ensure your account stays active.</p>
                <p>Are you sure you want to enable auto renewal?</p>
              </template>
              <template v-slot:actions>
                <gg-btn secondary @click="autorenew.confirm = false">No</gg-btn>
                <gg-btn primary :loading="autorenew.loading" @click="enableAutorenewNow">Yes</gg-btn>
              </template>
            </gg-modal>
            <renew-now-modal v-if="billing" ref="renewNowModal" :service-id="billing.id" />
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import kvm from '@/api/kvm'
import {
  formatRenewalDate,
  parseRenewalDate,
  percent,
  statusColor,
} from '@/helpers/helpers'
import KvmAppInfo from '@/views/services/hosting/kvm/KvmAppInfo'
import KvmCopyable from '@/views/services/hosting/kvm/KvmCopyable'
import KvmLogo from '@/views/services/hosting/kvm/KvmLogo'
import KvmSparkline from '@/views/services/hosting/kvm/KvmSparkline'
import KvmDualSparkline from '@/views/services/hosting/kvm/KvmDualSparkline'
import { kvmSize } from '@/views/services/hosting/kvm/kvmFormat'
import { cancellationTypes } from '@/consts/cancellations'
import RenewNowModal from 'Components/Services/Hosting/Shared/Overview/RenewNowModal'
import SharedServiceTag from 'Components/Common/SharedServiceTag'

export default {
  name: 'HostingKvmInfo',
  components: { KvmAppInfo, KvmCopyable, KvmLogo, KvmSparkline, KvmDualSparkline, RenewNowModal, SharedServiceTag },
  props: {
    // Provided by KvmServiceView via <router-view :server :loading :on-refresh>.
    server: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    // Lets a reset that reboots the VM trigger a re-fetch of the service.
    onRefresh: { type: Function, default: null },
  },
  data: () => ({
    // Metric history for the tile mini-sparklines (GET /vm/metrics). null until
    // loaded / when unavailable; the sparklines are v-if'd on having points.
    metrics: null,
    // Root password change flow (customer-supplied password).
    passwordDialog: false, // dialog open
    resetting: false, // request in flight
    reveal: false, // password shown vs masked
    password: '', // the new root password being entered
    allowReboot: false, // permit a brief restart if the offline path is needed
    formError: '', // inline dialog error (e.g. the 409 "needs a reboot" guidance)
    // The server's current root password, fetched from the server on first reveal.
    rootPw: { loading: false, shown: false, value: null, available: null, current: null, error: '' },
    hostnameDialog: { open: false, value: '', saving: false, error: '' },
    // Auto-renew enable flow (mirrors the shared-hosting account details card).
    autorenew: { ineligible: false, confirm: false, loading: false },
  }),
  computed: {
    ...mapGetters(['services']),
    // Backend-declared feature flags drive which cards light up vs grey out.
    capabilities () { return this.server.capabilities || {} },
    // Theme primary, so the mini-charts match the app's accent colour.
    primaryColor () {
      const theme = this.$vuetify.theme.currentTheme
      return (theme && theme.primary) || '#0ca750'
    },
    // Second traffic line (outgoing) — the theme's info blue, distinct from primary.
    trafficOutColor () {
      const theme = this.$vuetify.theme.currentTheme
      return (theme && theme.info) || '#1e88e5'
    },
    // Trend series for the tile sparklines (CPU as a percentage).
    cpuSeries () { return this.spark('cpu', 100) },
    memSeries () { return this.spark('mem') },
    netinSeries () { return this.spark('netin') },
    netoutSeries () { return this.spark('netout') },
    passwordValid () { return this.password.length >= 8 && this.password.length <= 128 },
    passwordRules () {
      return [
        v => (!!v && v.length >= 8) || 'At least 8 characters.',
        v => (!v || v.length <= 128) || 'At most 128 characters.',
      ]
    },
    serverRunning () { return this.server.power_state === 'online' },
    rootPwButton () {
      if (this.rootPw.shown) return { icon: 'mdi-eye-off', label: 'Hide root password' }
      if (this.rootPw.available === false) return { icon: 'mdi-refresh', label: 'Check for the root password again' }
      return { icon: 'mdi-eye', label: 'Show root password' }
    },
    statusChip () {
      return this.server.power_state === 'online'
        ? { text: 'Running', color: 'primary' }
        : { text: 'Stopped', color: 'secondary' }
    },
    sshCommand () {
      const ip = this.server.primary_ip
      return ip && ip !== '—' ? `ssh root@${ip}` : '—'
    },
    memPercent () { return this.percent(this.server.ram_used, this.server.ram_total) },
    memUsage () {
      if (this.server.ram_total == null) return 'Not available'
      return `${kvmSize(this.server.ram_used || 0)} / ${kvmSize(this.server.ram_total)}`
    },
    actionTiles () {
      return [
        { title: 'SSH Keys', sub: 'Manage', icon: 'mdi-key-variant', route: 'hosting_kvm_ssh' },
        { title: 'Terminal', sub: 'Open', icon: 'mdi-console-line', route: 'hosting_kvm_terminal' },
        ...(this.capabilities.firewall
          ? [{ title: 'Firewall', sub: 'Manage', icon: 'mdi-shield-outline', route: 'hosting_kvm_firewall' }]
          : []),
      ]
    },
    vpsDetails () {
      return [
        { label: 'Server location', value: this.server.location || '—' },
        { label: 'Operating system', value: this.server.os || '—' },
        { label: 'Hostname', value: this.server.hostname || '—', hostname: true },
        { label: 'Primary IP', value: this.server.primary_ip || '—', copy: true },
        { label: 'Root password', rootPassword: true },
        { label: 'Domain', value: this.server.domain || this.server.effective_domain || '—', copy: true },
        { label: 'vCPU', value: this.server.vcpu != null ? `${this.server.vcpu} cores` : '—' },
        { label: 'Memory', value: this.sizeLabel(this.server.ram_total) },
        { label: 'Disk', value: this.sizeLabel(this.server.disk_total) },
      ]
    },
    // Plan Details, laid out exactly like the shared-hosting "Hosting Account
    // Details" card: [left column, right column]. Billing comes from the
    // account's service list (amapi servicelist → WHMCS), not from Prox.
    planColumns () {
      const b = this.billing
      const left = [{ label: 'Plan Type', value: (b && b.name) || this.server.plan || '—' }]
      // No Password Management row for KVM: the root password is a server
      // setting, shown and changed in VPS Details above.
      const right = [
        { label: 'Sharing Options', sharing: true },
      ]
      if (b) {
        left.push(
          { label: 'Status', value: b.status, dot: statusColor(b.status) },
          { label: 'Billing Cycle', value: b.renewalterm || '—' },
          {
            label: 'Renewal Date',
            value: formatRenewalDate(parseRenewalDate(b.renewaldate)),
            ...(b.can_access_early_renewal ? { link: 'Renew Now', action: this.openRenewNow } : {}),
          },
        )
        right.push(b.autorenew
          ? {
            label: 'Auto Renew',
            value: 'Enabled',
            dot: statusColor(1),
            link: b.status === 'Suspended' ? 'Submit Cancellation' : 'Disable',
            to: { name: 'cancel', params: { service_id: b.id }, query: { type: cancellationTypes.HOSTING } },
          }
          : { label: 'Auto Renew', value: 'Disabled', dot: statusColor(0), link: 'Enable', action: this.startEnableAutorenew })
      }
      return [left, right]
    },
    // This server's billing record from the shared service list (the id we're
    // routed with is the billing service id).
    billing () {
      return (this.services || []).find(s => String(s.id) === String(this.server.id)) || null
    },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError', 'enableAutorenew']),
    // Split detail rows into two columns (left gets the extra one).
    columns (rows) {
      const half = Math.ceil(rows.length / 2)
      return [rows.slice(0, half), rows.slice(half)]
    },
    openRenewNow () {
      if (this.$refs.renewNowModal) this.$refs.renewNowModal.showModal()
    },
    startEnableAutorenew () {
      if (!this.billing.can_enable_autorenew) this.autorenew.ineligible = true
      else this.autorenew.confirm = true
    },
    async enableAutorenewNow () {
      this.autorenew.loading = true
      try {
        await this.enableAutorenew({
          service_id: this.billing.id,
          service_type: cancellationTypes.HOSTING,
          cancellation_id: this.billing.cancellation_id,
        })
      } finally {
        this.autorenew.loading = false
        this.autorenew.confirm = false
      }
    },
    kvmSize,
    percent,
    statusColor,
    sizeLabel (bytes) { return kvmSize(bytes) },
    openPasswordDialog () {
      this.password = ''
      this.reveal = false
      this.allowReboot = false
      this.formError = ''
      this.passwordDialog = true
    },
    closePasswordDialog () {
      this.passwordDialog = false
      this.password = ''
      this.reveal = false
      this.allowReboot = false
      this.formError = ''
    },
    // Fill the field with a strong random password (crypto-grade, no ambiguous chars).
    generatePassword () {
      const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_=+'
      const len = 20
      const arr = new Uint32Array(len)
      window.crypto.getRandomValues(arr)
      let out = ''
      for (let i = 0; i < len; i++) out += charset[arr[i] % charset.length]
      this.password = out
      this.reveal = true // show it so the customer can note/copy it
    },
    copyPassword () {
      if (!this.password) return
      navigator.clipboard.writeText(this.password)
      this.reportSuccess('Password copied')
    },
    // Set the customer-supplied root password (POST /vm/password-reset).
    async resetPassword () {
      if (!this.passwordValid) return
      try {
        this.resetting = true
        this.formError = ''
        const res = await kvm.resetPassword(this.server.id, this.password, this.allowReboot)
        this.reportSuccess(res.reboot
          ? 'Root password set — the server is restarting to apply it.'
          : 'Root password updated.')
        this.closePasswordDialog()
        // The server now holds the new password; fetch it fresh on the next reveal.
        this.forgetRootPassword()
        // The offline path restarts the VM; refresh so the dashboard reflects it.
        if (res.reboot && this.onRefresh) this.onRefresh()
      } catch (e) {
        // Agent down + reboot not allowed → backend asks us to retry allowing one.
        if (e.status === 409 && !this.allowReboot) {
          this.formError = e.message ||
            'The guest agent isn’t responding, so applying the password needs a brief reboot. ' +
            'Tick “Allow a brief reboot” above and try again.'
        } else {
          this.reportError(e)
        }
      } finally {
        this.resetting = false
      }
    },
    // Show / hide the root password. The first reveal reads it from the server
    // (GET /vm/root-password); later toggles don't refetch.
    async toggleRootPassword () {
      const pw = this.rootPw
      if (pw.shown) {
        pw.shown = false
        return
      }
      await this.loadRootPassword()
      pw.shown = !!pw.value
    },
    // Copy it without showing it (fetched from the server first if needed).
    async copyRootPassword () {
      await this.loadRootPassword()
      const pw = this.rootPw
      if (!pw.value) return // not on file / error — the row already says so
      try {
        await navigator.clipboard.writeText(pw.value)
        this.reportSuccess('Root password copied')
      } catch (e) {
        // Some browsers refuse a copy that follows a network wait: show it
        // instead so it can be copied by hand.
        pw.shown = true
        this.reportError(new Error('Couldn’t copy automatically — the root password is shown so you can copy it.'))
      }
    },
    // Fetch the root password from the server unless we already have it.
    async loadRootPassword () {
      const pw = this.rootPw
      if (pw.value != null) return
      pw.loading = true
      pw.error = ''
      try {
        const res = await kvm.getRootPassword(this.server.id)
        pw.available = res.available
        pw.value = res.password
        pw.current = res.current
      } catch (e) {
        pw.error = e.message || 'Couldn’t read the root password from the server.'
      } finally {
        pw.loading = false
      }
    },
    // RFC 1123 hostname / FQDN — the same rule the API enforces.
    hostnameValid (v) {
      const h = String(v || '').toLowerCase().replace(/\.$/, '')
      return h.length > 0 && h.length <= 253 &&
        /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/.test(h)
    },
    openHostnameDialog () {
      const current = this.server.hostname && this.server.hostname !== '—' ? this.server.hostname : ''
      this.hostnameDialog = { open: true, value: current, saving: false, error: '' }
    },
    async saveHostname () {
      const d = this.hostnameDialog
      d.saving = true
      d.error = ''
      try {
        const hostname = await kvm.setHostname(this.server.id, d.value)
        d.open = false
        this.reportSuccess(`Hostname changed to ${hostname}`)
        if (this.onRefresh) this.onRefresh()
      } catch (e) {
        d.error = e.message
      } finally {
        d.saving = false
      }
    },
    forgetRootPassword () {
      this.rootPw = { loading: false, shown: false, value: null, available: null, current: null, error: '' }
    },
    // Mini-sparkline values for a /vm/metrics series (null → 0). `scale` lets the
    // CPU fraction (0-1) be plotted as a percentage.
    spark (key, scale = 1) {
      const pts = (this.metrics && this.metrics[key]) || []
      return pts.map(p => (p[1] == null ? 0 : p[1] * scale))
    },
    // Pull metric history for the tile sparklines; only when the backend serves it.
    async loadMetrics () {
      if (!this.capabilities.metrics_history || !this.server.id) return
      try {
        this.metrics = await kvm.getMetrics(this.server.id, '1h')
      } catch (e) {
        this.metrics = null // sparklines just stay hidden if history is unavailable
      }
    },
  },
  watch: {
    // server arrives async from the parent route view; (re)load when its id lands.
    'server.id': { immediate: true, handler () { this.loadMetrics(); this.forgetRootPassword() } },
    // A rebuild is a new VM with a new root password: drop what we'd fetched.
    'server.vm_id' () { this.forgetRootPassword() },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.vds-metric {
  border: 1px solid $kvm-border-light;
  border-radius: 6px;
  height: 100%;
  min-height: 124px;

  .theme--dark & {
    border-color: $kvm-border-dark;
  }
}
.vds-metric__label,
.vds-metric__sub {
  @include kvm-secondary-text;
}
// Colour key for the two traffic lines (in / out).
.vds-legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

// Bold key/value labels in the summary header (Root access / Root password).
.vds-sublabel {
  @include kvm-secondary-text;
}

.vds-tile {
  display: block;
  transition: box-shadow 0.15s;

  cursor: pointer;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  &:focus-visible {
    @include kvm-focus-ring;
  }

  @include kvm-reduced-motion {
    transition: none;
  }
}
.vds-tile__sub {
  @include kvm-secondary-text;
}

// Compact "Label: value" rows, no dividers — like the shared-hosting
// "Hosting Account Details" card.
.vds-detail-row {
  flex-wrap: wrap;
  gap: 2px 16px;
  min-height: 28px;
  padding: 2px 0;
}
.vds-detail__label,
.vds-detail__value--muted {
  @include kvm-secondary-text;
}
// Renew Now / Disable / Enable — the portal's own link colour.
.vds-plan-link {
  white-space: nowrap;
  &:focus-visible { @include kvm-focus-ring; }
}

// The hidden root password, sized like the revealed value so the row doesn't jump.
.vds-rootpw-mask {
  font-family: monospace;
  letter-spacing: 0.08em;
}

// Inline "Change" trigger — a real <button> styled as a link (colour: primary--text).
.vds-link-btn {
  display: inline;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    @include kvm-focus-ring;
    border-radius: 2px;
  }
}

.vds-dialog-btn:focus-visible {
  @include kvm-focus-ring;
}

// Dense icon buttons (reveal / copy) inside the password field.
.vds-icon-btn {
  @include kvm-min-target(24px);

  &:focus-visible {
    @include kvm-focus-ring;
    border-radius: 4px;
  }
}

// Readable, theme-aware text for the inline warning (default amber fails AA).
.vds-form-alert.v-alert {
  color: #7a5b00 !important;
}
.theme--dark .vds-form-alert.v-alert {
  color: #ffcc66 !important;
}
</style>
